import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MarketingHeader } from '../components/MarketingHeader';
import { Clock, Calendar, BarChart2, Users, CheckCircle, ArrowRight } from 'lucide-react';

const Footer = lazy(() => import('../components/Footer'));

const CHALLENGES = [
  {
    icon: Clock,
    title: 'Manual time tracking',
    desc: 'Spreadsheets and paper timesheets lead to errors, disputes, and wasted hours every payroll cycle.',
    fix: 'Digital clock-in with PIN or QR. Timesheets auto-generated and manager-approved.',
  },
  {
    icon: Calendar,
    title: 'Scheduling conflicts',
    desc: 'Building schedules manually means constant back-and-forth, double-bookings, and last-minute scrambles.',
    fix: 'Conflict-free smart scheduling that matches shifts to real employee availability.',
  },
  {
    icon: BarChart2,
    title: 'No visibility into labor costs',
    desc: 'Without real-time data, labor costs spiral out of control — especially across multiple locations.',
    fix: 'Live labor cost dashboards and executive insights for every location.',
  },
  {
    icon: Users,
    title: 'Disorganized HR documents',
    desc: 'Lost policies, unsigned forms, and compliance gaps put your business at risk.',
    fix: 'Centralized secure document hub with acknowledgment tracking.',
  },
];

const STATS = [
  { value: '30+', label: 'minutes saved per manager per day' },
  { value: '95%', label: 'reduction in scheduling conflicts' },
  { value: '1', label: 'platform for your entire team' },
  { value: '30', label: 'day free trial, no card required' },
];

export default function WorkforceManagement() {
  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingHeader />

      <main>
        {/* Hero */}
        <section className="py-20 px-4 bg-white border-b border-slate-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-1.5 mb-6">
              <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Workforce Management Software</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
              Modern workforce management<br />
              <span className="text-orange-500">for any business</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              ReStaff is an all-in-one workforce management platform that replaces spreadsheets,
              paper schedules, and disconnected apps with a single tool your entire team will use.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                  Try Free for 30 Days <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              <Link to="/features">
                <Button variant="outline">See All Features</Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What is workforce management */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>
              What is workforce management?
            </h2>
            <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4">
              <p>
                Workforce management (WFM) is the set of processes businesses use to optimize employee
                productivity — covering scheduling, time tracking, attendance, HR documentation, and
                performance analytics. Effective WFM ensures the right people are in the right place
                at the right time, while keeping labor costs under control.
              </p>
              <p>
                For small and mid-sized businesses, workforce management used to require expensive
                enterprise software or hours spent in spreadsheets. ReStaff changes that by giving
                every business — from a single café to a franchise with 20 locations — enterprise-grade
                workforce tools at an affordable price.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 px-4 bg-orange-500">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {STATS.map(({ value, label }) => (
              <div key={label}>
                <div className="text-4xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }}>{value}</div>
                <div className="mt-1 text-sm text-orange-100">{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Common challenges */}
        <section className="py-20 px-4 bg-white border-t border-slate-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-4" style={{ fontFamily: 'Manrope' }}>
              Common workforce challenges — and how ReStaff solves them
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-xl mx-auto">
              Every business faces the same painful problems. Here's how ReStaff addresses each one.
            </p>
            <div className="grid sm:grid-cols-2 gap-8">
              {CHALLENGES.map(({ icon: Icon, title, desc, fix }) => (
                <div key={title} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500">
                      <Icon size={20} />
                    </div>
                    <h3 className="font-semibold text-slate-900">{title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">{desc}</p>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-green-800 font-medium">{fix}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Manrope' }}>
              Built for businesses of every type
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { title: 'Retail & Hospitality', desc: 'Manage hourly staff across peak periods with conflict-free scheduling and real-time attendance.' },
                { title: 'Multi-Location Chains', desc: 'Oversee all locations from a single dashboard. Compare performance, control costs, and stay compliant.' },
                { title: 'Growing Teams', desc: 'Start simple and scale. ReStaff grows with you — from 5 employees to 500, same platform.' },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-slate-900">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Start managing your workforce smarter
            </h2>
            <p className="mt-3 text-slate-400">30-day free trial. Cancel anytime, no questions asked. Setup in minutes.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}
